// The device UI's navigation model — SINGLE SOURCE for the two-level side
// menu. The local App.tsx renders it directly (adding setup/watchdog badges);
// the public cloud renders the same structure under each site's "Manage"
// area, prefixing routes with its site mount and disabling entries whose
// pages are not available over synced data.
//
// Labels are (i18n key, English default) pairs so both apps translate through
// their own i18next instance identically.
import {
  Activity,
  BarChart3,
  BatteryCharging,
  Cloud,
  Coins,
  Fuel,
  Gauge,
  HardDrive,
  History,
  Info,
  LayoutDashboard,
  LayoutGrid,
  Layers,
  Monitor,
  PlugZap,
  Receipt,
  Server,
  Settings,
  Settings2,
  SlidersHorizontal,
  Sun,
  Thermometer,
  Users as UsersIcon,
  Wifi,
  Zap,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

export type DeviceNavSectionId =
  | "dashboard"
  | "inverter"
  | "controller"
  | "optimization"
  | "billing"
  | "history"
  | "settings";

export interface DeviceNavItem {
  labelKey: string;
  labelDefault: string;
  icon: LucideIcon;
  to?: string;
  end?: boolean;
  disabled?: boolean;
  adminOnly?: boolean;
}

export interface DeviceNavSection {
  id: DeviceNavSectionId;
  labelKey: string;
  labelDefault: string;
  icon: LucideIcon;
  subItems: DeviceNavItem[];
}

export function isPathActive(pathname: string, to?: string): boolean {
  if (!to) return false;
  return pathname === to || pathname.startsWith(`${to}/`);
}

export const deviceNavSections: DeviceNavSection[] = [
  {
    id: "dashboard",
    labelKey: "nav.dashboard",
    labelDefault: "Dashboard",
    icon: LayoutDashboard,
    subItems: [
      { labelKey: "nav.dashboard", labelDefault: "Dashboard", icon: Activity, to: "/dashboard/graphics" },
      { labelKey: "nav.info", labelDefault: "Info", icon: Info, to: "/dashboard", end: true },
    ],
  },
  {
    id: "controller",
    labelKey: "nav.controller",
    labelDefault: "Controller",
    icon: Monitor,
    subItems: [
      { labelKey: "nav.operation", labelDefault: "Operation", icon: SlidersHorizontal, to: "/control" },
      { labelKey: "nav.relays", labelDefault: "Relays", icon: SlidersHorizontal, to: "/relays" },
      { labelKey: "nav.logs", labelDefault: "Logs", icon: History, to: "/controller/logs" },
      { labelKey: "nav.info", labelDefault: "Info", icon: Server, to: "/controller/system" },
    ],
  },
  {
    id: "optimization",
    labelKey: "nav.optimization",
    labelDefault: "Optimization",
    icon: Coins,
    subItems: [
      { labelKey: "nav.optimizationBattery", labelDefault: "Battery Optimization", icon: BatteryCharging, to: "/optimization", end: true },
      { labelKey: "nav.optimizationTariffBlock", labelDefault: "Tariff Block", icon: Layers, to: "/optimization/tariff-block" },
      { labelKey: "nav.history", labelDefault: "History", icon: BarChart3, to: "/optimization/history" },
    ],
  },
  {
    id: "billing",
    labelKey: "nav.billing",
    labelDefault: "Billing",
    icon: Receipt,
    subItems: [
      { labelKey: "nav.billing", labelDefault: "Billing", icon: Receipt, to: "/billing", end: true },
      { labelKey: "nav.billingSimulation", labelDefault: "Simulation", icon: SlidersHorizontal, to: "/billing/simulation" },
    ],
  },
  {
    id: "history",
    labelKey: "nav.history",
    labelDefault: "History",
    icon: BarChart3,
    subItems: [
      { labelKey: "history.cats.production", labelDefault: "Production", icon: Zap, to: "/history/production" },
      { labelKey: "history.cats.powerEnergy", labelDefault: "Power & energy", icon: Gauge, to: "/history/power-energy" },
      { labelKey: "history.cats.temperature", labelDefault: "Temperature", icon: Thermometer, to: "/history/temperature" },
      { labelKey: "history.cats.battery", labelDefault: "Battery", icon: BatteryCharging, to: "/history/battery" },
      { labelKey: "history.cats.all", labelDefault: "All", icon: LayoutGrid, to: "/history/all" },
    ],
  },
  {
    id: "inverter",
    labelKey: "nav.inverter",
    labelDefault: "Inverter",
    icon: Zap,
    subItems: [
      { labelKey: "nav.system", labelDefault: "System", icon: Info, to: "/system" },
      { labelKey: "nav.pv", labelDefault: "Solar", icon: Sun, to: "/pv" },
      { labelKey: "nav.battery", labelDefault: "Battery", icon: BatteryCharging, to: "/battery" },
      { labelKey: "nav.inverter", labelDefault: "Inverter", icon: Zap, to: "/inverter" },
      { labelKey: "nav.grid", labelDefault: "Grid", icon: PlugZap, to: "/grid" },
      { labelKey: "nav.generator", labelDefault: "Generator", icon: Fuel, to: "/generator" },
      { labelKey: "nav.diagnostics", labelDefault: "Diagnostics", icon: Activity, to: "/diagnostics" },
    ],
  },
  {
    id: "settings",
    labelKey: "nav.settings",
    labelDefault: "Settings",
    icon: Settings,
    subItems: [
      { labelKey: "nav.general", labelDefault: "General", icon: Settings2, to: "/general" },
      { labelKey: "nav.controller", labelDefault: "Controller", icon: HardDrive, to: "/firmware" },
      { labelKey: "nav.optimization", labelDefault: "Optimization", icon: Coins, to: "/optimization-settings" },
      { labelKey: "nav.network", labelDefault: "Network", icon: Wifi, to: "/network" },
      { labelKey: "nav.cloud", labelDefault: "Cloud", icon: Cloud, to: "/cloud" },
      { labelKey: "nav.inverter", labelDefault: "Inverter", icon: Settings, to: "/settings" },
      { labelKey: "nav.users", labelDefault: "Users", icon: UsersIcon, to: "/users", adminOnly: true },
    ],
  },
];
